'use client';

import { c as _c } from "react/compiler-runtime";
import { TEST_IDS } from '@repo/sdk/constants';
import { FieldContent, Tags, TagsContent, TagsEmpty, TagsGroup, TagsInput, TagsItem, TagsList, TagsTrigger, TagsValue } from '@repo/ui/components';
import { CheckIcon, PlusIcon, Trash2Icon } from '@repo/ui/icons';
import { cn } from '@repo/ui/lib/utils';
import { AnimatePresence, motion } from '@repo/ui/motion';
import { type SyntheticEvent, useState } from 'react';
import { ReadOnlyTooltip, useReadOnly } from '../../components/readonly.tsx';
import { useFieldContext } from '../../lib/form-context.ts';
interface FormTagsInputProps<T extends TagOption> {
  allowCustomTags?: boolean;
  'aria-label'?: string;
  availableTags: T[];
  container?: HTMLElement | null;
  contentClassName?: string;
  emptyMessage?: string;
  onSearchChange?: (value: string) => void;
  onTagAdd?: (value: string) => void; // slug for existing tags, name for new custom tags
  onTagDelete?: (tag: T) => void; // offers to delete each custom tag from the options
  onTagRemove?: (slug: string) => void;
  onTagsChange?: () => void;
  optionTags?: T[];
  placeholder?: string;
  shouldFilter?: boolean;
  variant?: 'ghost' | 'outline';
}

// Any catalogue entry with a slug and a name; system entries can't be deleted.
interface TagOption {
  is_system?: boolean;
  name: string;
  slug: string;
}
export const FormTagsInput = t0 => {
  const $ = _c(86);
  const {
    allowCustomTags: t1,
    "aria-label": ariaLabel,
    availableTags,
    container,
    contentClassName,
    emptyMessage,
    onSearchChange,
    onTagAdd,
    onTagDelete,
    onTagRemove,
    onTagsChange,
    optionTags,
    placeholder: t2,
    shouldFilter,
    variant: variantProp
  } = t0;
  const allowCustomTags = t1 === undefined ? true : t1;
  const placeholder = t2 === undefined ? "Search tag..." : t2;
  const field = useFieldContext();
  const isReadOnly = useReadOnly();
  const [newTag, setNewTag] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  let canCreateTag;
  let filteredOptionTags;
  let handleCreateTag;
  let handleRemove;
  let handleSelect;
  let selected;
  let t3;
  let trimmedTag;
  if ($[0] !== allowCustomTags || $[1] !== availableTags || $[2] !== field || $[3] !== newTag || $[4] !== onTagAdd || $[5] !== onTagRemove || $[6] !== onTagsChange || $[7] !== optionTags || $[8] !== shouldFilter) {
    selected = field.state.value ?? [];
    handleRemove = value => {
      if (!selected.includes(value)) {
        return;
      }
      const newValue = selected.filter(v => v !== value);
      field.handleChange(newValue);
      onTagRemove?.(value);
      onTagsChange?.();
    };
    handleSelect = value_0 => {
      if (selected.includes(value_0)) {
        handleRemove(value_0);
        return;
      }
      const newValue_0 = [...selected, value_0];
      field.handleChange(newValue_0);
      onTagAdd?.(value_0);
      onTagsChange?.();
    };
    const tagsForOptions = optionTags ?? availableTags;
    trimmedTag = newTag.trim();
    const hasExactMatch = [...tagsForOptions, ...availableTags].some(tag => tag.name.toLowerCase() === trimmedTag.toLowerCase() || tag.slug === trimmedTag);
    canCreateTag = allowCustomTags && trimmedTag.length > 0 && !hasExactMatch;
    handleCreateTag = () => {
      if (!canCreateTag) {
        setNewTag("");
        return;
      }
      const newValue_1 = [...selected, trimmedTag];
      field.handleChange(newValue_1);
      onTagAdd?.(trimmedTag);
      setNewTag("");
    };
    filteredOptionTags = shouldFilter === false ? tagsForOptions : tagsForOptions.filter(tag_0 => !trimmedTag || tag_0.name.toLowerCase().includes(trimmedTag.toLowerCase()));
    let t4;
    if ($[17] !== availableTags) {
      t4 = tag_1 => availableTags.find(t => t.slug === tag_1)?.name ?? tag_1;
      $[17] = availableTags;
      $[18] = t4;
    } else {
      t4 = $[18];
    }
    t3 = selected.map(t4).join(", ");
    $[0] = allowCustomTags;
    $[1] = availableTags;
    $[2] = field;
    $[3] = newTag;
    $[4] = onTagAdd;
    $[5] = onTagRemove;
    $[6] = onTagsChange;
    $[7] = optionTags;
    $[8] = shouldFilter;
    $[9] = canCreateTag;
    $[10] = filteredOptionTags;
    $[11] = handleCreateTag;
    $[12] = handleRemove;
    $[13] = handleSelect;
    $[14] = selected;
    $[15] = t3;
    $[16] = trimmedTag;
  } else {
    canCreateTag = $[9];
    filteredOptionTags = $[10];
    handleCreateTag = $[11];
    handleRemove = $[12];
    handleSelect = $[13];
    selected = $[14];
    t3 = $[15];
    trimmedTag = $[16];
  }
  const selectedNames = t3;
  if (isReadOnly) {
    const t4 = selectedNames || "-";
    let t5;
    if ($[19] !== t4) {
      t5 = <ReadOnlyTooltip>{t4}</ReadOnlyTooltip>;
      $[19] = t4;
      $[20] = t5;
    } else {
      t5 = $[20];
    }
    return t5;
  }
  let t4;
  if ($[21] !== availableTags || $[22] !== handleRemove || $[23] !== isOpen || $[24] !== selected || $[25] !== selectedNames || $[26] !== variantProp) {
    t4 = isOpen ? selected.map(tag_2 => <TagsValue key={tag_2} onRemove={() => handleRemove(tag_2)} variant="outline">{availableTags.find(t_0 => t_0.slug === tag_2)?.name ?? tag_2}</TagsValue>) : <span className={cn("text-left text-base font-normal text-wrap", variantProp === "outline" && "md:text-sm")}>{selectedNames}</span>;
    $[21] = availableTags;
    $[22] = handleRemove;
    $[23] = isOpen;
    $[24] = selected;
    $[25] = selectedNames;
    $[26] = variantProp;
    $[27] = t4;
  } else {
    t4 = $[27];
  }
  const selectedValue = t4;
  const t5 = !isOpen && !variantProp && "p-0";
  const t6 = variantProp === "outline" && "min-h-8 px-2.5 py-1";
  let t7;
  if ($[28] !== t5 || $[29] !== t6) {
    t7 = cn(t5, t6, "transition-all duration-100");
    $[28] = t5;
    $[29] = t6;
    $[30] = t7;
  } else {
    t7 = $[30];
  }
  const t8 = variantProp ?? (!isOpen ? "ghost" : "outline");
  let t9;
  if ($[31] !== selected.length || $[32] !== selectedValue) {
    t9 = selected.length === 0 ? <span className="text-muted-foreground">-</span> : selectedValue;
    $[31] = selected.length;
    $[32] = selectedValue;
    $[33] = t9;
  } else {
    t9 = $[33];
  }
  let t10;
  if ($[34] !== isOpen || $[35] !== placeholder) {
    t10 = isOpen && <motion.span animate={{
      opacity: 1
    }} className="text-muted-foreground px-2 py-px" exit={{
      opacity: 0
    }} initial={{
      opacity: 0
    }} transition={{
      duration: 0.15
    }}>{placeholder}</motion.span>;
    $[34] = isOpen;
    $[35] = placeholder;
    $[36] = t10;
  } else {
    t10 = $[36];
  }
  let t11;
  if ($[37] !== t10) {
    t11 = <AnimatePresence data-sentry-element="AnimatePresence" data-sentry-source-file="form-tags-input.tsx">{t10}</AnimatePresence>;
    $[37] = t10;
    $[38] = t11;
  } else {
    t11 = $[38];
  }
  let t12;
  if ($[39] !== ariaLabel || $[40] !== t11 || $[41] !== t7 || $[42] !== t8 || $[43] !== t9) {
    t12 = <TagsTrigger aria-label={ariaLabel} className={t7} variant={t8} data-sentry-element="TagsTrigger" data-sentry-source-file="form-tags-input.tsx">{t9}{t11}</TagsTrigger>;
    $[39] = ariaLabel;
    $[40] = t11;
    $[41] = t7;
    $[42] = t8;
    $[43] = t9;
    $[44] = t12;
  } else {
    t12 = $[44];
  }
  let t13;
  if ($[45] !== onSearchChange) {
    t13 = value_1 => {
      setNewTag(value_1);
      onSearchChange?.(value_1);
    };
    $[45] = onSearchChange;
    $[46] = t13;
  } else {
    t13 = $[46];
  }
  let t14;
  if ($[47] !== newTag || $[48] !== placeholder || $[49] !== t13) {
    t14 = <TagsInput onValueChange={t13} placeholder={placeholder} value={newTag} data-sentry-element="TagsInput" data-sentry-source-file="form-tags-input.tsx" />;
    $[47] = newTag;
    $[48] = placeholder;
    $[49] = t13;
    $[50] = t14;
  } else {
    t14 = $[50];
  }
  let t15;
  if ($[51] !== canCreateTag || $[52] !== emptyMessage || $[53] !== filteredOptionTags.length || $[54] !== shouldFilter) {
    t15 = !canCreateTag && (shouldFilter !== false || filteredOptionTags.length === 0) && <TagsEmpty><span className="text-muted-foreground text-sm">{emptyMessage ?? "No tags found"}</span></TagsEmpty>;
    $[51] = canCreateTag;
    $[52] = emptyMessage;
    $[53] = filteredOptionTags.length;
    $[54] = shouldFilter;
    $[55] = t15;
  } else {
    t15 = $[55];
  }
  let t16;
  if ($[56] !== filteredOptionTags || $[57] !== handleSelect || $[58] !== onTagDelete || $[59] !== selected) {
    let t17;
    if ($[61] !== handleSelect || $[62] !== onTagDelete || $[63] !== selected) {
      t17 = tag_3 => <TagsItem aria-label={tag_3.name} className="[&>svg:last-child]:hidden" key={tag_3.slug} onSelect={handleSelect} value={tag_3.slug}>{tag_3.name}<span className="relative flex items-center">{selected.includes(tag_3.slug) && <CheckIcon className={cn("text-muted-foreground", onTagDelete && !tag_3.is_system && "-translate-x-6.5 transition-transform duration-100 ease-[cubic-bezier(0.23,1,0.32,1)] motion-reduce:transition-none pointer-fine:translate-x-0 pointer-fine:group-hover/command-item:-translate-x-6.5 pointer-fine:group-hover/command-item:delay-100 pointer-fine:group-hover/command-item:duration-150 pointer-fine:group-has-[button:focus-visible]/command-item:-translate-x-6.5 pointer-fine:group-has-[button:focus-visible]/command-item:delay-0")} size={14} />}{onTagDelete && !tag_3.is_system && <button aria-label={`Delete tag ${tag_3.name}`} className="text-muted-foreground hover:text-destructive focus-visible:ring-ring absolute top-1/2 right-0 -translate-y-1/2 rounded-sm p-0.5 transition-opacity duration-0 ease-[cubic-bezier(0.23,1,0.32,1)] focus-visible:pointer-events-auto focus-visible:opacity-100 focus-visible:ring-2 focus-visible:delay-0 focus-visible:outline-none pointer-fine:pointer-events-none pointer-fine:opacity-0 pointer-fine:group-hover/command-item:pointer-events-auto pointer-fine:group-hover/command-item:opacity-100 pointer-fine:group-hover/command-item:delay-100 pointer-fine:group-hover/command-item:duration-150" data-testid={TEST_IDS.form.tagDeleteButton(tag_3.slug)} onClick={event => {
            stopItemEvent(event);
            onTagDelete(tag_3);
          }} onKeyDown={stopItemEvent} onPointerDown={stopItemEvent} type="button"><Trash2Icon size={14} /></button>}</span></TagsItem>;
      $[61] = handleSelect;
      $[62] = onTagDelete;
      $[63] = selected;
      $[64] = t17;
    } else {
      t17 = $[64];
    }
    t16 = filteredOptionTags.map(t17);
    $[56] = filteredOptionTags;
    $[57] = handleSelect;
    $[58] = onTagDelete;
    $[59] = selected;
    $[60] = t16;
  } else {
    t16 = $[60];
  }
  let t17;
  if ($[65] !== t16) {
    t17 = <TagsGroup data-sentry-element="TagsGroup" data-sentry-source-file="form-tags-input.tsx">{t16}</TagsGroup>;
    $[65] = t16;
    $[66] = t17;
  } else {
    t17 = $[66];
  }
  let t18;
  if ($[67] !== canCreateTag || $[68] !== handleCreateTag || $[69] !== trimmedTag) {
    t18 = canCreateTag && <TagsGroup forceMount={true}><TagsItem forceMount={true} onSelect={handleCreateTag} value={`create:${trimmedTag}`}><span className="flex items-center gap-2"><PlusIcon className="text-muted-foreground" size={14} />Create new tag: {trimmedTag}</span></TagsItem></TagsGroup>;
    $[67] = canCreateTag;
    $[68] = handleCreateTag;
    $[69] = trimmedTag;
    $[70] = t18;
  } else {
    t18 = $[70];
  }
  let t19;
  if ($[71] !== t15 || $[72] !== t17 || $[73] !== t18) {
    t19 = <TagsList data-sentry-element="TagsList" data-sentry-source-file="form-tags-input.tsx">{t15}{t17}{t18}</TagsList>;
    $[71] = t15;
    $[72] = t17;
    $[73] = t18;
    $[74] = t19;
  } else {
    t19 = $[74];
  }
  let t20;
  if ($[75] !== container || $[76] !== contentClassName || $[77] !== shouldFilter || $[78] !== t14 || $[79] !== t19) {
    t20 = <TagsContent className={contentClassName} container={container} shouldFilter={shouldFilter} data-sentry-element="TagsContent" data-sentry-source-file="form-tags-input.tsx">{t14}{t19}</TagsContent>;
    $[75] = container;
    $[76] = contentClassName;
    $[77] = shouldFilter;
    $[78] = t14;
    $[79] = t19;
    $[80] = t20;
  } else {
    t20 = $[80];
  }
  let t21;
  if ($[81] !== isOpen || $[82] !== newTag || $[83] !== t12 || $[84] !== t20) {
    t21 = <FieldContent data-sentry-element="FieldContent" data-sentry-component="FormTagsInput" data-sentry-source-file="form-tags-input.tsx"><Tags className="w-full" onOpenChange={setIsOpen} open={isOpen} setValue={setNewTag} value={newTag} data-sentry-element="Tags" data-sentry-source-file="form-tags-input.tsx">{t12}{t20}</Tags></FieldContent>;
    $[81] = isOpen;
    $[82] = newTag;
    $[83] = t12;
    $[84] = t20;
    $[85] = t21;
  } else {
    t21 = $[85];
  }
  return t21;
};

// Keeps a delete button's events from reaching its option, which would toggle the tag.
const stopItemEvent = (event: SyntheticEvent) => {
  event.stopPropagation();
};